import { useParams, Link, useNavigate } from 'react-router-dom';
import { guideApi } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Card, CardBody, ErrorAlert } from '../../components/ui';
import { ArrowLeft, BookOpen } from 'lucide-react';

// Tag color mapping (same as GuidePage)
const tagColors: Record<string, string> = {
  'Getting Started': 'bg-green-100 text-green-700 border-green-200',
  'Tasks': 'bg-blue-100 text-blue-700 border-blue-200',
  'Credits': 'bg-amber-100 text-amber-700 border-amber-200',
  'FAQ': 'bg-purple-100 text-purple-700 border-purple-200',
  'Account': 'bg-pink-100 text-pink-700 border-pink-200',
  'Submissions': 'bg-cyan-100 text-cyan-700 border-cyan-200',
};

function getTagColor(tag: string): string {
  return tagColors[tag] || 'bg-gray-100 text-gray-700 border-gray-200';
}

// Format date
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function GuideDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useApiQuery({
    queryKey: ['guide-section', id],
    queryFn: () => guideApi.getSection(Number(id)),
    enabled: !!id,
  });

  const section = data;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        <Link
          to="/guide"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Guides
        </Link>
        <ErrorAlert message={error?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (!section) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        <Link
          to="/guide"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Guides
        </Link>

        <Card>
          <CardBody className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Guide not found.</p>
            <button
              onClick={() => navigate('/guide')}
              className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Browse all guides
            </button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Back Navigation */}
      <Link
        to="/guide"
        className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Guides
      </Link>

      {/* Content Card */}
      <Card>
        <CardBody className="p-6 md:p-8">
          {/* Header */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {section.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              {section.tags.map((tag) => (
                <span
                  key={tag}
                  className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getTagColor(tag)}`}
                >
                  {tag}
                </span>
              ))}
              <span className="text-sm text-gray-400">
                Updated {formatDate(section.updated_at)}
              </span>
            </div>
          </div>

          {/* Content */}
          <div
            className="prose prose-primary max-w-none prose-headings:mt-6 prose-headings:mb-3 prose-p:my-3 prose-ul:my-3 prose-ol:my-3 prose-li:my-1 prose-img:rounded-lg prose-img:shadow-md"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        </CardBody>
      </Card>
    </div>
  );
}
